/**
 * Hide-from-view filter for the recycle UI. Per design §5.5 the v1 UI
 * does NOT expose a permanent-purge tool — "delete" in the UI means
 * "stop showing me this row" via a localStorage filter. The filter is
 * keyed by store-id and scoped to the current user-browser pair.
 *
 * <p>Operations are intentionally simple (a {@code Set<string>}
 * round-tripped to JSON) so a future server-side variant could swap
 * in without changing call sites — same {@code isHidden} /
 * {@code hide} / {@code unhide} surface.
 */

const STORAGE_KEY = "datumcraft.operate.recycle.hidden";

function load(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter((x): x is string => typeof x === "string"));
  } catch {
    return new Set();
  }
}

function save(s: Set<string>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(s)));
  } catch {
    /* fail open — Safari private mode etc. */
  }
}

export function loadHidden(): Set<string> {
  return load();
}

export function isHidden(storeId: string, hidden: Set<string>): boolean {
  return hidden.has(storeId);
}

export function hideOne(storeId: string): Set<string> {
  const next = load();
  next.add(storeId);
  save(next);
  return next;
}

export function hideMany(storeIds: string[]): Set<string> {
  const next = load();
  for (const id of storeIds) next.add(id);
  save(next);
  return next;
}

export function unhideOne(storeId: string): Set<string> {
  const next = load();
  next.delete(storeId);
  save(next);
  return next;
}

export function clearHidden(): Set<string> {
  save(new Set());
  return new Set();
}
